import React, { FC, ReactNode, createContext, useEffect, useMemo, useRef, useState } from 'react';
import { parseJwt, getLocalStorage, setLocalStorage, parseJWTEntitlements } from '@src/utils';
import { useLDClient, useFlags } from 'gatsby-plugin-launchdarkly';

import { UserEntitlements, useGetUserQuery } from '@backend';
import type {
  Subscription,
  ChargifyPaymentProfile,
  PaymentMethod,
  WorkoutFavorites,
} from '@backend';
import { TIERS, TIER_DEFAULT } from '@constants';
import { isEqual } from 'lodash';
import { ApolloError } from '@apollo/client';
import { refreshTokenWithRetry } from '../../../gatsby-theme-acquisition/src/utils/utilities';

const TIERS_AV = Object.entries(TIERS).map((i) => i[1]);

interface ProviderProps {
  children?: ReactNode;
}

enum Tier {
  PREMIUM = 'premium',
  BASE = 'base',
}

export interface UserContext {
  isLoading: boolean;
  hasActiveSubscription: boolean | null;
  vimeoUserId: string | null;
  givenName: string | null;
  familyName: string | null;
  isNewUser: boolean | null;
  isEntitledToFreeTrial: boolean | null;
  userId: string | null;
  chargifyUserId: string | null;
  type: string | null;
  signupChannel: string | null;
  lmodSubscription: Subscription | null;
  userEntitlements: UserEntitlements | null;
  chargifyPaymentProfile: ChargifyPaymentProfile | null;
  paymentMethod: PaymentMethod | null;
  programFavorites: string[] | null;
  addressStreet: string | null;
  addressStreet2: string | null;
  addressCountry: string | null;
  addressPostalCode: string | null;
  addressRegion: string | null;
  addressStateCode: string | null;
  addressLocality: string | null;
  locale: string | null;
  tier: Tier | null;
  email: string | null;
  tuneAffiliateId: string | null;
  subComms: boolean | null;
  subCommsConfirmed: boolean | null;
  refetchUser?: () => void;
  error?: null | ApolloError;
  workoutFavorites: WorkoutFavorites[] | null;
  hasLegacyPlan: boolean | null;
}

const userContext = createContext<UserContext>({
  isLoading: true,
  hasActiveSubscription: null,
  vimeoUserId: null,
  givenName: null,
  familyName: null,
  isNewUser: null,
  isEntitledToFreeTrial: null,
  userId: null,
  chargifyUserId: null,
  type: null,
  signupChannel: null,
  userEntitlements: null,
  lmodSubscription: null,
  chargifyPaymentProfile: null,
  paymentMethod: null,
  programFavorites: null,
  addressStreet: null,
  addressStreet2: null,
  addressCountry: null,
  addressPostalCode: null,
  addressRegion: null,
  addressStateCode: null,
  addressLocality: null,
  locale: null,
  tier: null,
  email: null,
  tuneAffiliateId: null,
  subComms: null,
  subCommsConfirmed: null,
  workoutFavorites: null,
  hasLegacyPlan: null,
});

const getTierName = (tierId: String) => {
  // safe guard against anything being in the tier for now
  const name = tierId?.split('#')[1];
  return TIERS_AV.includes(name) ? name : TIER_DEFAULT;
};

const UserProvider: FC<ProviderProps> = ({ children }) => {
  const { rtJwtEntitlement } = useFlags();

  // Keep existing GraphQL logic for fallback
  const { loading, data, refetch, error } = useGetUserQuery();
  const { getUser: user } = data || {};

  const ldClient = useLDClient();

  const refetchUser = () => refetch();

  // Bumped after a background JWT refresh succeeds to force a re-render, since the
  // localStorage read below is synchronous and won't otherwise re-run on its own.
  const [, setJwtRefreshVersion] = useState(0);

  // Parse JWT synchronously during render so derived values are correct on first render
  const jwtTokenRaw = typeof window !== 'undefined' ? localStorage.getItem('jwtToken') : null;
  const jwtToken = rtJwtEntitlement && jwtTokenRaw ? parseJWTEntitlements(jwtTokenRaw) : null;

  // The JWT can be stale (e.g. minted before a subscription was activated) while GraphQL
  // already reflects the up-to-date entitlement. When that happens, trust GraphQL and
  // kick off a background refresh so the JWT catches up for subsequent loads.
  const jwtHasActiveSubscription = jwtToken?.hasActiveSubscription ?? false;

  const isStaleJwtEntitlement =
    rtJwtEntitlement && !jwtHasActiveSubscription && user?.lmodEntitlement === true;

  const refreshTriggeredRef = useRef(false);
  useEffect(() => {
    if (isStaleJwtEntitlement && !refreshTriggeredRef.current) {
      refreshTriggeredRef.current = true;
      refreshTokenWithRetry()
        .then(() => {
          // Force a re-render so the synchronous localStorage read above picks up
          // the refreshed JWT, correcting tier/vimeoUserId/userId as well.
          setJwtRefreshVersion((version) => version + 1);
        })
        .catch((refreshError) => {
          refreshTriggeredRef.current = false;
          // eslint-disable-next-line no-console
          console.error('Failed to refresh stale JWT entitlement:', refreshError);
        });
    }
  }, [isStaleJwtEntitlement]);

  // Use JWT data for entitlements when feature flag enabled, otherwise use GraphQL
  const hasActiveSubscription = rtJwtEntitlement
    ? jwtHasActiveSubscription || isStaleJwtEntitlement
    : user?.lmodEntitlement ?? null;
  const tier = rtJwtEntitlement
    ? jwtToken?.tier ?? TIER_DEFAULT
    : getTierName(parseJwt(localStorage.getItem('jwtToken'))?.tierId ?? '');
  const vimeoUserId = rtJwtEntitlement ? jwtToken?.vimeoUserId ?? null : user?.vimeoUserId ?? null;
  const userId = rtJwtEntitlement ? jwtToken?.userId ?? null : user?.userId ?? null;

  if (rtJwtEntitlement ? jwtToken : user) {
    // Calling `identify` will cause the flags to be re-evaluated for the new
    // user that's logged in. Changes in flag values will stream in and could.
    // If this is a problem, set the flags inside the promise returned
    // from `ldClient.identify`.
    // can access flags anywhere in the app by calling `useFlags()`
    // set updated User Info to local storage

    // Update localStorage for GraphQL data (needed for amplitude tracking and other features)
    if (user) {
      const existingUser = JSON.parse(getLocalStorage('user-info') || 'null');
      if (!isEqual(user, existingUser)) {
        setLocalStorage('user-info', JSON.stringify(user));
      }
    }

    ldClient?.identify({
      key: userId,
      custom: { tierId: tier as string },
    });
  }

  const hasLegacyPlan =
    !!(user as any)?.currentPlan && (user as any)?.currentPlan?.finishDate > Date.now();

  const providerValue = useMemo(
    () => ({
      isLoading: loading,
      hasActiveSubscription,
      tier,
      vimeoUserId,
      // Always use GraphQL for profile fields to ensure consistency after updates
      givenName: user?.givenName ?? null,
      familyName: user?.familyName ?? null,
      email: user?.email ?? null,
      userId,
      error,
      refetchUser,
      hasLegacyPlan,
      // Always include GraphQL fields for backward compatibility and amplitude tracking
      isNewUser: (user as any)?.isNewUser ?? null,
      isEntitledToFreeTrial: (user as any)?.isEntitledToFreeTrial ?? null,
      chargifyUserId: user?.chargifyUserId ?? null,
      type: user?.type ?? null,
      signupChannel: (user?.signupChannel as string | null) ?? null,
      lmodSubscription: (user as any)?.lmodSubscription ?? null,
      userEntitlements: user?.userEntitlements ?? null,
      chargifyPaymentProfile: user?.chargifyPaymentProfile ?? null,
      paymentMethod: user?.paymentMethod ?? null,
      programFavorites: (user as any)?.programFavorites ?? null,
      addressStreet: (user as any)?.addressStreet ?? null,
      addressStreet2: (user as any)?.addressStreet2 ?? null,
      addressCountry: (user as any)?.addressCountry ?? null,
      addressPostalCode: (user as any)?.addressPostalCode ?? null,
      addressRegion: (user as any)?.addressRegion ?? null,
      addressStateCode: (user as any)?.addressStateCode ?? null,
      addressLocality: (user as any)?.addressLocality ?? null,
      locale: (user as any)?.locale ?? null,
      tuneAffiliateId: (user as any)?.tuneAffiliateId ?? null,
      subComms: (user as any)?.subComms ?? null,
      subCommsConfirmed: (user as any)?.subCommsConfirmed ?? null,
      workoutFavorites: (user as any)?.workoutFavorites ?? null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, hasActiveSubscription, tier, vimeoUserId, userId, error, hasLegacyPlan, user]
  );

  return <userContext.Provider value={providerValue}>{children}</userContext.Provider>;
};

export { userContext, UserProvider };
